import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const ConnectGoogle = () => {
  const [user, setUser] = useState(null);
  const [pendingLink, setPendingLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const authError = params.get('auth_error');
    const message = params.get('message');
    if (authError) {
      setError(message || 'Could not connect Google. Try again.');
    }

    axios.get('/api/auth/status', { withCredentials: true })
      .then((res) => {
        const sessionUser = res.data.user;
        if (sessionUser?.pendingGoogleLink) {
          setPendingLink(sessionUser);
          return;
        }
        if (!res.data.isAuthenticated || sessionUser?.newUser) {
          navigate('/', { replace: true });
          return;
        }
        if (sessionUser.googleId) {
          navigate('/', { replace: true });
          return;
        }
        setUser(sessionUser);
      })
      .catch(() => navigate('/', { replace: true }))
      .finally(() => setLoading(false));
  }, [location.search, navigate]);

  const connectHref = `/api/auth/google?returnTo=${encodeURIComponent('/')}`;

  const confirmLink = async () => {
    setBusy(true);
    setError('');
    try {
      await axios.post('/api/auth/google/confirm-link', {}, { withCredentials: true });
      window.location.href = '/';
    } catch (err) {
      setError(err.response?.data?.error || 'Could not link Google to that account.');
      setBusy(false);
    }
  };

  const cancelLink = async () => {
    setBusy(true);
    try {
      await axios.post('/api/auth/google/cancel-link', {}, { withCredentials: true });
    } catch (err) {
      console.error('Cancel Google link failed:', err);
    }
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        Loading…
      </div>
    );
  }

  if (pendingLink) {
    const username = pendingLink.existingUsername || 'that account';
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-gray-900">
          <h1 className="text-2xl font-bold mb-2">Link this Google account?</h1>
          <p className="text-gray-600 mb-4">
            This email is already on <span className="font-semibold">{username}</span>.
            Link Google to keep that account, XP, and halls. A new account will
            not be created.
          </p>
          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={confirmLink}
            className="block w-full text-center bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded-lg mb-3"
          >
            Link to {username}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={cancelLink}
            className="block w-full text-center text-sm text-gray-500 hover:text-gray-800"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 p-4">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-gray-900">
        <h1 className="text-2xl font-bold mb-2">Connect Google</h1>
        <p className="text-gray-600 mb-4">
          {user?.username ? `Hi ${user.username}. ` : ''}
          BIGPLAYS now uses Google as the account login. Link Google to keep this
          Steam account, XP, and halls.
        </p>
        {error && (
          <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>
        )}
        <a
          href={connectHref}
          className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg mb-3"
        >
          Continue with Google
        </a>
        <button
          type="button"
          onClick={async () => {
            await axios.get('/api/auth/logout', { withCredentials: true });
            window.location.href = '/';
          }}
          className="block w-full text-center text-sm text-gray-500 hover:text-gray-800"
        >
          Log out
        </button>
      </div>
    </div>
  );
};

export default ConnectGoogle;
